import {
    SharingAudience, SharingLinkKind, ISharingLink, SharingRole,
    ClientId, ISharingInformation, ICustomLabeledAudienceConfig, Mode
} from '../interfaces/SharingInterfaces';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import stringsResx from '../components/Share.resx';
import { IIconData } from '../components/PermissionsList/ShareLink/ChangePermission/ChangePermission';
import * as ClientIdHelper from '../utilities/ClientIdHelper';
import { ITextField } from 'office-ui-fabric-react/lib/TextField';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
export const days = [stringsResx.sunday, stringsResx.monday, stringsResx.tuesday, stringsResx.wednesday, stringsResx.thursday, stringsResx.friday, stringsResx.saturday];
export const shortDays = [stringsResx.shortSunday, stringsResx.shortMonday, stringsResx.shortTuesday, stringsResx.shortWednesday, stringsResx.shortThursday, stringsResx.shortFriday, stringsResx.shortSaturday];
export const shortMonths = [stringsResx.shortJanuary, stringsResx.shortFebruary, stringsResx.shortMarch, stringsResx.shortApril, stringsResx.shortMay, stringsResx.shortJune, stringsResx.shortJuly, stringsResx.shortAugust, stringsResx.shortSeptember, stringsResx.shortOctober, stringsResx.shortNovember, stringsResx.shortDecember];
export const months = [stringsResx.january, stringsResx.february, stringsResx.march, stringsResx.april, stringsResx.may, stringsResx.june, stringsResx.july, stringsResx.august, stringsResx.september, stringsResx.october, stringsResx.november, stringsResx.december];
export const MAX_DAYS_FOR_EXPIRING_LINK = 730;
export enum ExpirationErrorCode {
    none,
    pastDate,
    adminPolicy,
    overMax,
    adminRequired
}

export function truncateItemNameForHeader(name: string): string {
    return _truncateItemName(name, 26);
}

export function truncateOrganizationName(name: string, maxLength?: number): string {
    maxLength = maxLength || 25;
    return _truncateItemName(name, maxLength);
}

export function truncateItemNameForShareNotification(name: string, maxLength?: number): string {
    maxLength = maxLength || 20;
    return _truncateItemName(name, maxLength);
}

function _truncateItemName(name: string, maxLength: number): string {
    if (!name) {
        return '';
    }

    const MAX_LENGTH = maxLength;
    const PART_LENGTH = (MAX_LENGTH / 2);

    if (name.length <= MAX_LENGTH) {
        return name;
    }

    const firstPart = name.slice(0, PART_LENGTH);
    const secondPart = name.slice((name.length) - PART_LENGTH);

    return `${firstPart}...${secondPart}`;
}

export function isLoadingData(sharingInformation: ISharingInformation) {
    return sharingInformation.userRole === SharingRole.none &&
        sharingInformation.defaultLinkKind === SharingLinkKind.uninitialized;
}

export function getSharingLinkKindFromVroomScope(scope: string, canEdit: boolean): SharingLinkKind {
    switch (scope) {
        case 'anonymous':
            return canEdit ? SharingLinkKind.anonymousEdit : SharingLinkKind.anonymousView;
        case 'organization':
            return canEdit ? SharingLinkKind.organizationEdit : SharingLinkKind.organizationView;
        case 'users':
            return SharingLinkKind.flexible;
        default:
            return SharingLinkKind.direct;
    }
}

export function getSharingAudienceFromVroomScope(scope: string): SharingAudience {
    switch (scope) {
        case 'anonymous':
            return SharingAudience.anyone;
        case 'organization':
            return SharingAudience.organization;
        case 'users':
            return SharingAudience.specificPeople
        default:
            return SharingAudience.existing
    }
}

export function getVroomScopeFromSharingLinkKind(linkKind: SharingLinkKind): string {
    switch (linkKind) {
        case SharingLinkKind.anonymousEdit:
        case SharingLinkKind.anonymousView:
            return 'anonymous';
        case SharingLinkKind.organizationEdit:
        case SharingLinkKind.organizationView:
            return 'organization';
        case SharingLinkKind.flexible:
            return 'users';
        default:
            return 'existingAccess';
    }
}

export function getLinkIconUrl(sharingLink: ISharingLink, customLabeledAudienceConfig?: ICustomLabeledAudienceConfig): string {
    const linkKind = sharingLink.sharingLinkKind;
    const audience = sharingLink.audience;
    const iconBaseUrl = 'https://static2.sharepointonline.com/files/fabric/assets/icons/sharing';
    switch (linkKind) {
        case SharingLinkKind.organizationView:
        case SharingLinkKind.organizationEdit:
            return `${iconBaseUrl}/companyonly.svg`;
        case SharingLinkKind.anonymousView:
        case SharingLinkKind.anonymousEdit:
            return `${iconBaseUrl}/anyone.svg`;
        case SharingLinkKind.flexible:
            if (sharingLink.audience === SharingAudience.customLabeledSpecificPeople) {
                return customLabeledAudienceConfig ? customLabeledAudienceConfig.iconUrl : `${iconBaseUrl}/chat.svg`;
            } else {
                return `${iconBaseUrl}/specificpeople.svg`;
            }
        default:
            if (audience === SharingAudience.customLabeledSpecificPeople) {
                return customLabeledAudienceConfig ? customLabeledAudienceConfig.iconUrl : `${iconBaseUrl}/chat.svg`;
            } else {
                return `${iconBaseUrl}/existingaccess.svg`;
            }
    }
}

export function getLinkIconName(sharingLink: ISharingLink): string {
    const linkKind = sharingLink.sharingLinkKind;
    switch (linkKind) {
        case SharingLinkKind.organizationView:
        case SharingLinkKind.organizationEdit:
            return 'Work';
        case SharingLinkKind.anonymousView:
        case SharingLinkKind.anonymousEdit:
            return 'Globe';
        case SharingLinkKind.flexible:
            if (sharingLink.audience === SharingAudience.anyone) {
                return 'Globe';
            } else if (sharingLink.audience === SharingAudience.organization) {
                return 'Work';
            } else {
                return 'PeopleAdd';
            }
        default:
            return 'Link';
    }
}

/* tslint:disable-next-line:no-any */
export function getDarkTheme(): any {
    return {
        palette: {
            themePrimary: "#3A96DD",
            themeLighterAlt: "#eff6fc",
            themeLighter: "#deecf9",
            themeLight: "#c7e0f4",
            themeTertiary: "#71afe5",
            themeSecondary: "#2b88d8",
            themeDarkAlt: "#106ebe",
            themeDark: "#004578",
            themeDarker: "#34365c",
            neutralLighterAlt: "#292827",
            neutralLighter: "#323130",
            neutralLight: "#3B3A39",
            neutralQuaternaryAlt: "#484644",
            neutralQuaternary: "#605E5C",
            neutralTertiaryAlt: "#484644",
            neutralTertiary: "#A19F9D",
            neutralSecondary: "#BEBBB8",
            neutralPrimaryAlt: "#E1DFDD",
            neutralPrimary: "#F3F2F1",
            neutralDark: "#FAF9F8",
            redDark: "#FF4343",
            black: "#FFFFFF",
            white: "#252423",
            primaryBackground: "#f9f8f7",
            primaryText: "#252423",
            bodyBackground: "#252423",
            bodyText: "#252423",
            disabledBackground: "#3B3A39",
            disabledText: "#c8c8c8"
        }
    };
}

export function getCustomLinkLabel(
    clientId: ClientId,
    labelType: 'edit' | 'view' | 'review',
    config: ICustomLabeledAudienceConfig): string {
    const isOutlook = ClientIdHelper.isOutlook(clientId);
    const audienceType = (config && config.audienceType) || (isOutlook && 'recipient');

    switch (audienceType) {
        case 'channel':
            switch (labelType) {
                case 'edit':
                    return stringsResx.peopleInChannelEditLabel;
                case 'review':
                    return stringsResx.peopleInChannelReviewLabel;
                default:
                    return stringsResx.peopleInChannelViewLabel;
            }
            break;
        case 'team':
            switch (labelType) {
                case 'edit':
                    return stringsResx.peopleInTeamEditLabel;
                case 'review':
                    return stringsResx.peopleInTeamReviewLabel;
                default:
                    return stringsResx.peopleInTeamViewLabel;
            }
            break;
        case 'recipient':
            switch (labelType) {
                case 'edit':
                    return stringsResx.recipientsCanEditLabel;
                case 'review':
                    return stringsResx.recipientsCanReviewLabel;
                default:
                    return stringsResx.recipientsCanViewLabel;
            }
            break;
        default:
            switch (labelType) {
                case 'edit':
                    return stringsResx.peopleInChatEditLabel;
                case 'review':
                    return stringsResx.peopleInChatReviewLabel;
                default:
                    return stringsResx.peopleInChatViewLabel;
            }
            break;
    }
}

export function getLinkLabel(
    sharingLink: ISharingLink,
    companyName: string,
    clientId: ClientId,
    customLabeledAudienceConfig?: ICustomLabeledAudienceConfig): string {
    const linkKind = sharingLink.sharingLinkKind;
    const audience = sharingLink.audience;

    switch (linkKind) {
        case SharingLinkKind.organizationView:
            return StringHelper.format(stringsResx.cslViewDescription, companyName);
        case SharingLinkKind.organizationEdit:
            return StringHelper.format(stringsResx.cslEditDescription, companyName);
        case SharingLinkKind.anonymousView:
            return stringsResx.anonViewDescription;
        case SharingLinkKind.anonymousEdit:
            return stringsResx.anonEditDescription;
        case SharingLinkKind.direct:
            if (audience === SharingAudience.customLabeledSpecificPeople) {
                // This means flexible links are disabled
                if (sharingLink.isEdit) {
                    return customLabeledAudienceConfig && !!customLabeledAudienceConfig.entityName ?
                        StringHelper.format(stringsResx.customLabeledEditLabel, customLabeledAudienceConfig.entityName) :
                        getCustomLinkLabel(clientId, 'edit', customLabeledAudienceConfig);
                } else if (sharingLink.isReview) {
                    return customLabeledAudienceConfig && !!customLabeledAudienceConfig.entityName ?
                        StringHelper.format(stringsResx.customLabeledReviewLabel, customLabeledAudienceConfig.entityName) :
                        getCustomLinkLabel(clientId, 'review', customLabeledAudienceConfig);
                } else {
                    return customLabeledAudienceConfig && !!customLabeledAudienceConfig.entityName ?
                        StringHelper.format(stringsResx.customLabeledViewLabel, customLabeledAudienceConfig.entityName) :
                        getCustomLinkLabel(clientId, 'view', customLabeledAudienceConfig);
                }
            } else {
                return stringsResx.existingPeopleDescription;
            }
        case SharingLinkKind.flexible:
            if (audience === SharingAudience.anyone) {
                if (sharingLink.blockDownload) {
                    if (sharingLink.hasPassword) {
                        return stringsResx.anonymousViewOnlyWithPassword;
                    } else {
                        return stringsResx.anonymousViewOnlyHint;
                    }
                }

                if (sharingLink.hasPassword) {
                    if (sharingLink.isEdit) {
                        return stringsResx.anonPasswordEditDescription;
                    } else {
                        return stringsResx.anonPasswordViewDescription;
                    }
                } else {
                    if (sharingLink.isEdit) {
                        return stringsResx.anonEditDescription;
                    } else {
                        return stringsResx.anonViewDescription;
                    }
                }
            } else if (audience === SharingAudience.customLabeledSpecificPeople) {
                if (sharingLink.isEdit) {
                    return customLabeledAudienceConfig && !!customLabeledAudienceConfig.entityName ?
                        StringHelper.format(stringsResx.customLabeledEditLabel, customLabeledAudienceConfig.entityName) :
                        getCustomLinkLabel(clientId, 'edit', customLabeledAudienceConfig);
                } else if (sharingLink.isReview === true) {
                    return customLabeledAudienceConfig && !!customLabeledAudienceConfig.entityName ?
                        StringHelper.format(stringsResx.customLabeledReviewLabel, customLabeledAudienceConfig.entityName) :
                        getCustomLinkLabel(clientId, 'review', customLabeledAudienceConfig);
                } else {
                    return customLabeledAudienceConfig && !!customLabeledAudienceConfig.entityName ?
                        StringHelper.format(stringsResx.customLabeledViewLabel, customLabeledAudienceConfig.entityName) :
                        getCustomLinkLabel(clientId, 'view', customLabeledAudienceConfig);
                }
            } else {
                if (sharingLink.audience === SharingAudience.organization) {
                    if (sharingLink.blockDownload) {
                        return StringHelper.format(stringsResx.cslViewOnlyHint, companyName);
                    }

                    if (sharingLink.isEdit) {
                        return StringHelper.format(stringsResx.cslEditDescription, companyName);
                    } else {
                        return StringHelper.format(stringsResx.cslViewDescription, companyName);
                    }
                } else {
                    if (sharingLink.blockDownload) {
                        return stringsResx.specificPeopleViewOnlyHint;
                    }

                    if (sharingLink.isEdit) {
                        return stringsResx.pslEditLabel;
                    } else if (sharingLink.isReview === true) {
                        return stringsResx.pslReviewLabel;
                    } else {
                        return stringsResx.pslViewLabel;
                    }
                }
            }
        default:
            return '';
    }

}

export function getLinkParticipants(link: ISharingLink): IPerson[] {
    let participants = [];
    let principals = link.principals;
    let invitees = link.invitees;
    for (let principal of principals) {
        participants.push({
            id: principal.id,
            name: principal.primaryText,
            image: principal.imageUrl,
            isResolved: true,
            principalName: principal.loginName,
            userId: principal.loginName,
            email: principal.email,
            job: principal.secondaryText,
            isExternal: principal.isExternal,
            loginName: principal.loginName,
            principalType: principal.type,
        });
    }

    for (let invitee of invitees) {
        let currentInvitee = [];
        if (invitee.isExternal === true) {
            currentInvitee = participants.filter(participant => participant.email === invitee.email);
        } else {
            currentInvitee = participants.filter(participant => participant.principalName === invitee.loginName);
        }
        if (currentInvitee.length === 0) {
            participants.push({
                id: invitee.id,
                name: invitee.primaryText,
                image: invitee.imageUrl,
                isResolved: true,
                principalName: invitee.loginName,
                userId: invitee.loginName,
                email: invitee.email,
                job: invitee.secondaryText,
                isExternal: invitee.isExternal,
                loginName: invitee.loginName,
                principalType: invitee.type,
            });
        }
    }

    return participants;
}

/**
 * Gets length of message text using the same algorithm as the server.
 * Logic copied from devmainoverride/sporel/otools/inc/sts/template/HtmlEncodeAllowSimpleTextLength.jss.
 *
 * - Turns ' ' after newline into '&nbsp;'.
 * - Turns ' ' follow by another ' ' into '&nbsp;'.
 * - Turns newline into '<br>'.
 * - Turns carriage return into ' '.
 */
export function getEncodedMessageLength(messageText: string): number {
    if (!messageText) {
        return 0;
    }

    let htmlLength: number = 0;
    let messageTextLength: number = messageText.length;
    let messageTextIterator: number = 0;
    let waitingForNonWhitespaceAfterBr: boolean = false;

    if (messageText.length === 0) {
        return 0;
    }

    const HTML_STRINGS = {
        Nbsp: "&#160;",
        Br: "<br />",
        Quot: "&quot;&quot;&quot;",
        Amp: "&amp;",
        Apostrophe: "&#39;",
        Lt: "&lt;",
        Gt: "&gt;",
        Space: " "
    };

    while (messageTextIterator < messageTextLength) {
        let specialChar: string = null;
        let character: string = messageText[messageTextIterator];

        // Replace the current character if necessary.
        if (character === ' ') {
            if (messageTextIterator + 1 < messageTextLength && messageText[messageTextIterator + 1] === ' ' || waitingForNonWhitespaceAfterBr) {
                specialChar = HTML_STRINGS.Nbsp;
            } else {
                specialChar = ' ';
            }
        } else if (character === '\n') {
            specialChar = HTML_STRINGS.Br;
            waitingForNonWhitespaceAfterBr = true;
        } else {
            if (character === '"') {
                specialChar = HTML_STRINGS.Quot;
            } else if (character === '&') {
                specialChar = HTML_STRINGS.Amp;
            } else if (character === '\'') {
                specialChar = HTML_STRINGS.Apostrophe;
            } else if (character === '<') {
                specialChar = HTML_STRINGS.Lt;
            } else if (character === '>') {
                specialChar = HTML_STRINGS.Gt;
            } else if (character === '\r') {
                specialChar = HTML_STRINGS.Space;
            }

            waitingForNonWhitespaceAfterBr = false;
        }

        // Increment the message length.
        if (specialChar !== null) {
            htmlLength += specialChar.length;
        } else {
            htmlLength += 1;
        }

        // Increment iterator.
        messageTextIterator++;
    }

    return htmlLength;
}
export function getFileShareTypeFromAudience(audience: SharingAudience) {
    if (audience === SharingAudience.anyone) {
        return 0 /* anyone */;
    } else if (audience === SharingAudience.organization) {
        return 1 /* workGroup */;
    } else if (audience === SharingAudience.existing) {
        return 4 /* existing */;
    } else {
        return 2 /* specificPeople */;
    }
}
export function getRoleIconNameAndTitle(selectedKey: SharingRole): IIconData {
    let iconName: string;
    let iconTitle: string;
    switch (selectedKey) {
        case SharingRole.edit:
            iconName = 'Edit';
            iconTitle = stringsResx.canEditRole;
            break;
        case SharingRole.view:
            iconName = 'View';
            iconTitle = stringsResx.canViewRole;
            break;
        case SharingRole.review:
            iconName = 'Feedback';
            iconTitle = stringsResx.canReviewRole;
            break;
    }
    return {
        name: iconName,
        title: iconTitle
    };
}

export function getExpirationErrorCode(selectedDate: Date, linkExpirationRestrictionDays: number): ExpirationErrorCode {
    let expirationErrorCode = ExpirationErrorCode.none;
    const ONE_DAY_IN_MS = 86400000;
    const anonymousLinkExpirationRestrictionDays = linkExpirationRestrictionDays;

    // Return if no date is selected.
    if (!selectedDate || selectedDate.getTime() === (new Date(0)).getTime()) {
        return expirationErrorCode;
    }

    // Get "today", and set time equal to the selectedDate time (now).
    let today = new Date();
    today.setHours(selectedDate.getHours(), selectedDate.getMinutes(), selectedDate.getSeconds(), selectedDate.getMilliseconds());

    // Need to "floor" to account for DST.
    const numberOfDays = Math.floor((selectedDate.getTime() - today.getTime()) / ONE_DAY_IN_MS);

    // Needs to be at least today + 1.
    // Can't be more than max days (if set).
    // Can't be more than system supports.
    if (numberOfDays < 1) {
        expirationErrorCode = ExpirationErrorCode.pastDate;
    } else if (numberOfDays > MAX_DAYS_FOR_EXPIRING_LINK) {
        expirationErrorCode = ExpirationErrorCode.overMax;
    } else if (numberOfDays > anonymousLinkExpirationRestrictionDays && anonymousLinkExpirationRestrictionDays !== -1) {
        expirationErrorCode = ExpirationErrorCode.adminPolicy;
    }

    return expirationErrorCode;
}

export function getLinkKind(audience: SharingAudience, isEdit: boolean): SharingLinkKind {
    if (audience === SharingAudience.anyone) {
        return isEdit ? SharingLinkKind.anonymousEdit : SharingLinkKind.anonymousView;
    } else if (audience === SharingAudience.organization) {
        return isEdit ? SharingLinkKind.organizationEdit : SharingLinkKind.organizationView;
    } else if (audience === SharingAudience.specificPeople || audience === SharingAudience.existing) {
        return SharingLinkKind.direct;
    } else {
        return SharingLinkKind.flexible;
    }
}

export function getExpirationDatePickerErrorMessage(expirationErrorCode: ExpirationErrorCode, anonymousLinkExpirationRestrictionDays: number) {
    const allStrings = stringsResx;

    switch (expirationErrorCode) {
        case ExpirationErrorCode.pastDate:
            return allStrings.pastDateExpirationError;
        case ExpirationErrorCode.adminPolicy:
            return StringHelper.format(allStrings.adminExpirationError, anonymousLinkExpirationRestrictionDays);
        case ExpirationErrorCode.overMax:
            return StringHelper.format(allStrings.maxExpirationError, MAX_DAYS_FOR_EXPIRING_LINK);
        case ExpirationErrorCode.adminRequired:
            return stringsResx.requireExpirationError;
        default:
            return '';
    }
}

export function isCustomLabeledSpecificPeopleSupported(
    clientId: ClientId,
    mode: Mode,
    customLabeledAudienceConfig: ICustomLabeledAudienceConfig,
    hasRecipients: boolean): boolean {
    return ClientIdHelper.isOutlook(clientId) ||
        (hasRecipients &&
            mode === Mode.linkSettings &&
            (ClientIdHelper.isTeams(clientId) &&
                (customLabeledAudienceConfig && (!!customLabeledAudienceConfig.entityName || !!customLabeledAudienceConfig.audienceType))));
}

export function getDefaultExpirationDateValue(expirationRestrictionDays: number) {
    const requiredExpirationDays = expirationRestrictionDays;
    if (requiredExpirationDays === -1) {
        return null;
    } else {
        // Get "tomorrow" and at the "end of the day".
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + requiredExpirationDays);
        return tomorrow;
    }
}

export async function copySharingLinkToClipboard(
    clientId: ClientId,
    textField: ITextField,
    linkUrl: string) {
    let successfullyCopied = false;

    if (!ClientIdHelper.isMobile(clientId)) {
        /**
         * Attempt to copy via the browser.
         */
        try {
            textField.focus();
            textField.setSelectionRange(0, textField.value.length);
            successfullyCopied = document.execCommand('copy');
        } catch (error) {
            // Error handled below.
        }

        /* tslint:disable:no-string-literal */
        if (!successfullyCopied && window.navigator && window.navigator['clipboard']) {
            try {
                await window.navigator['clipboard'].writeText(textField.value);
                successfullyCopied = true;
            } catch (err) {
                // Error handled below.
            }
        }
        /* tslint:enable: no-string-literal */
    }

    // Attempt to copy to clipboard via external JavaScript.
    // We do this even in cases where document.execCommand('copy') indicates success since we have seen cases
    // where it does not really succeed, so we will always fallback on the native implementation if available.
    try {
        const externalJavaScript: any = window.external;
        const externalCopyResult = externalJavaScript.CopyToClipboard(linkUrl);

        /**
         * First iteration of CopyToClipboard did not have a return value; assume it's successful
         * if nothing is returned.
         */
        successfullyCopied = externalCopyResult || externalCopyResult === undefined;
    } catch (error) {
        // Error handled below.
    }

    return successfullyCopied;
}